import React, { useEffect, useState } from 'react';
import { BellIcon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu';
import NotificationService from '../../service/noti-service/Notification.service';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';

interface Notification {
  id: number;
  title: string;
  content: string;
  notificationType: string;
  isRead: boolean;
  notificationDate: string;
}

const NotificationBell: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);

  // Lấy customerId từ localStorage - điều chỉnh theo cách lưu trữ trong ứng dụng của bạn
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const customerId = user.customerId;

  const fetchNotifications = async () => {
    if (!customerId) return;

    setLoading(true);
    try {
      const data = await NotificationService.getUnreadNotifications(customerId);
      setNotifications(data);
    } catch (error) {
      console.error('Lỗi khi lấy thông báo:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id: number) => {
    try {
      await NotificationService.markAsRead(id);
      // Xóa thông báo đã đọc khỏi danh sách
      setNotifications((prev) => prev.filter((notif) => notif.id !== id));
    } catch (error) {
      console.error('Lỗi khi đánh dấu đã đọc:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();

    // Cập nhật thông báo mỗi 2 phút
    const interval = setInterval(fetchNotifications, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [customerId]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="relative p-2 rounded-full hover:bg-gray-300 transition-colors cursor-pointer">
          <BellIcon size={24} />
          {notifications.length > 0 && (
            <div className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
              {notifications.length > 9 ? '9+' : notifications.length}
            </div>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="z-50 bg-white border border-gray-200 rounded-md shadow-lg p-2 w-72 max-h-[400px] overflow-y-auto">
        <div className="flex justify-between items-center border-b pb-2 mb-2">
          <div className="font-semibold">Thông báo</div>
          {notifications.length > 0 && (
            <div className="text-xs text-gray-500">
              {notifications.length} chưa đọc
            </div>
          )}
        </div>

        {loading ? (
          <div className="py-4 text-center text-gray-500">Đang tải...</div>
        ) : notifications.length > 0 ? (
          <div className="space-y-2">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className="p-2 rounded-md hover:bg-gray-100 cursor-pointer border-l-4 border-blue-500"
                onClick={() => handleMarkAsRead(notification.id)}
              >
                <div className="font-medium">{notification.title}</div>
                <div className="text-sm text-gray-600">
                  {notification.content}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {formatDistanceToNow(
                    new Date(notification.notificationDate),
                    {
                      addSuffix: true,
                      locale: vi,
                    }
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-4 text-center text-gray-500">
            Không có thông báo mới
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
